import ContentConfig from '../../config/ContentConfig';
import logger from '../../log';
import { DeliveryMode, PlayType, SourceType } from '../constants';
import getModeAttemptOrder from './getModeAttemptOrder';
import checkDeliveryMode, { Result } from './checkDeliveryMode';
import requestPlayType from '../utils/requestPlayType';
import { ElevenAgentData } from '../../ecdn11/interfaces';
import { PrimingData } from '../../priming/interfaces';
import { ReporterSections } from '../Reporter/constants';
import { dispatchUpdateReportEvent } from '../../events/dispatch';

const log = logger.getLogger(__filename);

const { LIVE, VOD } = PlayType;

export interface Detection extends Result {
  playType: PlayType;
  fail?: boolean;
  modeOrder?: DeliveryMode[];
}

export const updateReportFor11 = (data?: ElevenAgentData): void => {
  if (!data) {
    log.debug('ECDN11 agent data not available');
    return;
  }
  const { version: v, host: machineName } = data;
  const agentId = data.agentId || data.agentID;
  const value = { agent: { agentId, machineName, v } };
  const section = ReporterSections.ECDN11;
  dispatchUpdateReportEvent({ section, value });
};

/**
 * Returns a Promise resolving to details about the delivery mode and platform that should be used
 * May reject the Promise if play type request fails
 * Intended mostly for internal use
 * @param options - Options for source detection
 * @returns detection object containing selected mode
 */
export default async function detectForSource({
  agentData,
  allowedModes,
  playType,
  playTypeOverridden,
  source
}: {
  agentData: PrimingData;
  allowedModes?: DeliveryMode[];
  playType: PlayType;
  playTypeOverridden?: boolean;
  source: { url: string; sourceType: SourceType };
}): Promise<Detection> {
  const config = ContentConfig.getInstance();
  log.debug('detectForSource:enter:', {
    source,
    allowedModes,
    agentData
  });

  // Determines play type by requesting manifest, or if manifest has already been requested, it
  // returns the saved result
  const getPlayType = async (
    previous?: PlayType,
    isOverridden?: boolean
  ): Promise<PlayType> => {
    if (isOverridden && previous) {
      return Promise.resolve(previous);
    }
    return requestPlayType(source).catch(err => {
      log.warn(
        'Could not determine play type due to failed request. Using LIVE',
        err
      );
      switch (previous) {
        // We only care about previously decided playtype if we fail. And then we only care if it was previously known
        case LIVE:
        case VOD:
          return Promise.resolve(previous);
        default:
          return Promise.resolve(LIVE);
      }
    });
  };

  const checkOrder = async (
    modes: DeliveryMode[],
    playType: PlayType
  ): Promise<Detection> => {
    // iterate and get results
    const ps = modes.map(async (mode: DeliveryMode): Promise<Detection> => {
      try {
        const res = await checkDeliveryMode(mode, config, agentData);

        if (res.mode === DeliveryMode.ECDN11) {
          updateReportFor11(res.elevenData);
        }
        return { ...res, playType };
      } catch (e) {
        return { mode, playType, fail: true };
      }
    });

    const vals = await Promise.all(ps);
    log.debug('After delivery checks:', vals);
    const successfulModes = vals.filter(r => !r.fail);

    // clear out failed modes
    const [first] = successfulModes;
    // if it's 11, get modeOrder
    const modeOrder = successfulModes.map(v => v.mode);
    return { ...first, modeOrder };
  };

  const modeOrder = getModeAttemptOrder(config, allowedModes, agentData);
  log.debug('detectForSource:pre:', modeOrder);
  // if playType is is unknown then we should treat it as undefined for the attemptNext loop
  const pt = playType === PlayType.UNKNOWN ? undefined : playType;
  // Always get the play type up front
  return getPlayType(pt, playTypeOverridden)
    .then(async pt2 => {
      return checkOrder(
        pt2 === VOD ? modeOrder[VOD] : modeOrder[LIVE],
        pt2
      ).then(result => ({
        ...result,
        playType: pt2
      }));
    })
    .then(result => {
      // inspect mode if mode is 11 ish, return stripped priority
      // ex. webrtc return be
      log.debug('detectForSource:end', { result });
      return result;
    });
}
